import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/api';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('Token mancante');
      return;
    }

    authService
      .verifyEmail(token)
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error');
        setErrorMsg(
          err.response?.data?.error?.message || 'Token non valido o scaduto'
        );
      });
  }, [token]);

  const handleResend = async () => {
    if (!resendEmail.trim()) return;
    try {
      await authService.resendVerification(resendEmail.trim());
      setResendSent(true);
    } catch {
      // ignore
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 3,
        background: 'transparent',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 4,
          border: '1px solid var(--border-color)',
          background: 'var(--surface)',
          boxShadow: 'var(--shadow-card)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ height: 5, background: 'var(--gradient-main)' }} />
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {status === 'loading' && (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Verifica in corso...
              </Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircleOutline sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Email verificata!
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Il tuo account e stato attivato. Ora puoi accedere alla piattaforma.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/login')}>
                Vai al Login
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <ErrorOutline sx={{ fontSize: 56, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                Verifica fallita
              </Typography>
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                {errorMsg}
              </Alert>

              {!resendSent ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Inserisci la tua email per ricevere un nuovo link di verifica:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      type="email"
                      placeholder="La tua email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <Button variant="outlined" onClick={handleResend} disabled={!resendEmail.trim()}>
                      Rinvia
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Alert severity="success" sx={{ mt: 2, textAlign: 'left' }}>
                  Se l'email esiste, riceverai un nuovo link di verifica.
                </Alert>
              )}

              <Button
                variant="text"
                onClick={() => navigate('/login')}
                sx={{ mt: 2, color: 'var(--accent-cyan)' }}
              >
                Torna al Login
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifyEmailPage;
